import React, { useEffect } from 'react';
import styled from 'styled-components';

import Skeleton from 'components/Skeleton';
import Tooltip from 'components/Tooltip';
import describeComparison from 'formatters/describeComparison';
import useMetric from 'hooks/useMetric';
import useValueFormatters from 'hooks/useValueFormatters';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import {
  getDeltaPercent,
  getRatioPercent,
  isTargetMet,
} from '../compositeUtils';
import { RatioCaption, RatioDenominator, formatPercent } from '../NumberRatio';
import NumberSubtext from '../NumberSubtext';
import ProgressBar from '../ProgressBar';
import SetupPlaceholder from '../SetupPlaceholder';
import useFittedFontSize from '../useFittedFontSize';
import useComparisonPeriod from '../useComparisonPeriod';
import useStudioMetricValue from '../useStudioMetricValue';
import { useLegendNumber } from './compositeLegend';

// The Number's rows, centred in a row taller than they need
const FitBox = styled.div<{ $isCentred: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => (props.$isCentred ? 'center' : 'flex-start')};
  gap: 2px;
  min-width: 0;
  min-height: 0;

  /* Its label and captions keep their height */
  > * {
    flex-shrink: 0;
  }
`;

const ValueLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  white-space: nowrap;
`;

const Value = styled.div<{ $fontSize: number }>`
  font-size: ${(props) => props.$fontSize}px;
  font-weight: 600;
  line-height: 1.15;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Caption = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: ${(props) => props.$color};
  font-size: 13px;
  white-space: nowrap;
`;

// The change beside the value is never squeezed: the value shrinks to fit
// instead
const DeltaSlot = styled.div`
  flex-shrink: 0;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-bottom: 4px;
`;

// The icon over a pale tint of its own colour
const IconTile = styled.div<{ $color: string }>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-color: ${(props) => props.$color};
    opacity: 0.14;
  }

  > * {
    position: relative;
  }
`;

const TARGET_OPERATOR_LABELS: Record<
  NonNullable<Studio.CompositeElement['target']>['operator'],
  string
> = { gte: '≥', lte: '≤' };

// The least a row's scale draws a Number at
const MIN_ROW_FONT_SIZE = 12;

const SUCCESS = 'var(--color-status-success)';
const ERROR = 'var(--color-status-error)';
const MUTED = 'var(--color-text-tertiary)';

// "↗ +4.2%" or "from 34%", next to the value
const ComparisonCaption = ({
  element,
  comparison,
  against,
  metric,
  current,
}: {
  element: Studio.CompositeElement;
  comparison: NonNullable<Studio.CompositeElement['comparison']>;
  against: PersistedComparisonType;
  metric: Metrics.Metric;
  current: number | undefined;
}) => {
  const previous = useStudioMetricValue({ metric, comparison: against });
  const testId = `studio-number-delta-${element.id}`;

  if (previous.isLoading || previous.isError) {
    return null;
  }
  if (comparison.display === 'from') {
    return (
      <DeltaSlot>
        <Caption $color={MUTED} data-testid={testId}>
          from {previous.formattedValue}
        </Caption>
      </DeltaSlot>
    );
  }
  const delta = getDeltaPercent({ current, previous: previous.value });
  if (delta === undefined) {
    return null;
  }
  const isHigherBetter = comparison.isHigherBetter ?? true;
  const color =
    Math.abs(delta) < 0.05
      ? MUTED
      : delta > 0 === isHigherBetter
        ? SUCCESS
        : ERROR;
  return (
    <DeltaSlot>
      {/* What it's against is too long to sit beside the value, so it's on
          hover. Every one following the report's is against the same. */}
      <Tooltip
        content={
          comparison.compareType === 'report'
            ? undefined
            : describeComparison(against)
        }
        position="top"
      >
        <Caption $color={color} data-testid={testId}>
          {delta >= 0 ? '↗' : '↘'}{' '}
          {formatPercent({ value: delta, isSigned: true })}
        </Caption>
      </Tooltip>
    </DeltaSlot>
  );
};

// A Number element inside a Composite block: a headline value with optional
// denominator, comparison and target captions. The value is at most
// fontSize (which follows the block's size), shrunk to fit its cell's
// width, and no larger than its row's scale lets it be. Its row never
// squeezes it: rows keep what their Numbers need.
const NumberElement = ({
  element,
  fontSize: maxFontSize,
  isCentred,
  blockMetricIds,
  progressColor,
  rowScale = 1,
  isSetupHintShown = false,
  onFit,
}: {
  element: Studio.CompositeElement;
  fontSize: number;
  // In a row taller than it needs
  isCentred: boolean;
  // Every metric in the Composite, which the subtext can show
  blockMetricIds: string[];
  // The progress bar's (and icon's) colour: the Composite's accent, unless
  // the Number stands for a chart series
  progressColor: string;
  // The share of its largest size the row draws its Numbers at, so they
  // shrink together
  rowScale?: number;
  // Told how far this Number has had to shrink to fit on its own
  onFit?: (fit: { elementId: string; scale: number }) => void;
  // Without a metric yet, its placeholder asks for it to be selected (in
  // Studio, while it isn't)
  isSetupHintShown?: boolean;
}) => {
  const [metricId] = element.metricIds ?? [];
  const metric = useMetric(metricId);
  const denominatorMetric = useMetric(element.denominatorMetricId);
  const { formatMetric } = useValueFormatters();
  const current = useStudioMetricValue({ metric });
  const against = useComparisonPeriod(element.comparison);
  const denominator = useStudioMetricValue({ metric: denominatorMetric });
  // Coloured like its series when it's a chart's legend
  const legend = useLegendNumber(metricId);
  const color = legend?.color ?? progressColor;
  const {
    fontSize: fittedFontSize,
    wrapperRef,
    valueRef,
    lineRef,
  } = useFittedFontSize({
    maxFontSize,
    fitsHeight: false,
    fitKey: [
      current.isLoading,
      current.formattedValue,
      denominator.formattedValue,
      !!against,
      !!element.showProgressBar,
      !!element.target,
      element.icon,
      element.label,
      element.subtext,
      element.denominatorLabel,
    ].join('|'),
  });
  // Its own fit is measured from whatever size it's drawn at, so it's still
  // known when the row draws it smaller
  useEffect(() => {
    if (onFit) {
      onFit({ elementId: element.id, scale: fittedFontSize / maxFontSize });
    }
  }, [element.id, fittedFontSize, maxFontSize, onFit]);
  const fontSize = Math.min(
    fittedFontSize,
    Math.max(MIN_ROW_FONT_SIZE, Math.round(maxFontSize * rowScale)),
  );

  // Without a metric yet, the placeholder a Number added to the canvas has
  // (see EmptyBlock): a faded sample Number. Its cell pads it already.
  if (!metricId) {
    return (
      <SetupPlaceholder
        visType="number"
        isHintShown={isSetupHintShown}
        isElement
        isFlush
        testId={`studio-number-empty-${element.id}`}
      />
    );
  }

  const label = element.label || (metric ? metric.name : 'Unknown metric');
  const ratio = denominatorMetric
    ? getRatioPercent({
        numerator: current.value,
        denominator: denominator.value,
      })
    : undefined;
  const targetMet = element.target
    ? isTargetMet({ value: current.value, target: element.target })
    : undefined;
  const targetColor =
    targetMet === undefined ? MUTED : targetMet ? SUCCESS : ERROR;

  return (
    <FitBox ref={wrapperRef} $isCentred={isCentred}>
      {element.icon ? (
        <LabelRow>
          <IconTile
            $color={color}
            data-testid={`studio-number-icon-${element.id}`}
          >
            <Icon icon={element.icon} color={color} width={16} height={16} />
          </IconTile>
          <Typography.Body type="Body 13" color={MUTED} isEllipsis>
            {label}
          </Typography.Body>
        </LabelRow>
      ) : (
        <Typography.Body type="Body 13" color={MUTED} isEllipsis>
          {label}
        </Typography.Body>
      )}
      <ValueLine ref={lineRef}>
        {current.isLoading && metric ? (
          <Skeleton width="60%" height={fontSize} />
        ) : (
          <Value
            ref={valueRef}
            $fontSize={fontSize}
            data-testid={`studio-number-value-${element.id}`}
          >
            {current.isError ? '!' : current.formattedValue}
            {denominatorMetric && (
              <RatioDenominator
                data-testid={`studio-number-ratio-${element.id}`}
              >
                {' / '}
                {denominator.isLoading ? '…' : denominator.formattedValue}
              </RatioDenominator>
            )}
          </Value>
        )}
        {metric && element.comparison && against && !current.isLoading && (
          <ComparisonCaption
            element={element}
            comparison={element.comparison}
            against={against}
            metric={metric}
            current={current.value}
          />
        )}
      </ValueLine>
      {denominatorMetric && element.showProgressBar && (
        <ProgressBar
          percent={ratio}
          color={color}
          testId={`studio-number-progress-${element.id}`}
        />
      )}
      {denominatorMetric && ratio !== undefined && (
        <RatioCaption
          ratio={ratio}
          label={element.denominatorLabel || denominatorMetric.name}
          testId={`studio-number-ratio-caption-${element.id}`}
        />
      )}
      {element.target && metric && (
        <Caption
          $color={targetColor}
          data-testid={`studio-number-target-${element.id}`}
        >
          <Icon
            icon={targetMet === false ? 'cross' : 'checkmark'}
            color={targetColor}
            width={14}
            height={14}
          />
          {[
            'Target',
            TARGET_OPERATOR_LABELS[element.target.operator],
            formatMetric({ value: element.target.value, draftMetric: metric }),
            element.target.label,
          ]
            .filter(Boolean)
            .join(' ')}
        </Caption>
      )}
      <NumberSubtext
        text={element.subtext}
        metricIds={blockMetricIds}
        testId={`studio-number-subtext-${element.id}`}
      />
    </FitBox>
  );
};

export default NumberElement;
